import React, { useEffect, useState } from "react";
import { TextField, CircularProgress, Autocomplete } from "@mui/material";
import axios from "axios";

// Utility function for debouncing
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const BankAutocomplete = ({ values, handleChange, handleBlur, setIfscCode, setIsIfscManual }) => {
    const [bankList, setBankList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [hasMore, setHasMore] = useState(true);

    // Debounced search value
    const debouncedSearch = useDebounce(search, 500);

    const fetchBanks = async (searchBy = null) => {
        if (!hasMore) return;

        setLoading(true);
        try {
            const token = sessionStorage.getItem("jwtToken");

            const response = await axios.get(
                `https://api.ipaisa.co.in/myapp/auth/instantpay/bankList`,
                {
                    params: { searchBy: searchBy || "", page: 0, size: 10 },
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (response.data && response.data.body.data) {
                setBankList(response.data.body.data);
                setHasMore(response.data.body.data.length > 0);
            }
        } catch (error) {
            console.error("Error fetching bank list:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (debouncedSearch.length >= 3) {
            fetchBanks(debouncedSearch);
        } else if (debouncedSearch === "") {
            fetchBanks();
        }
    }, [debouncedSearch]);

    const handleInputChange = (event, value) => {
        setSearch(value);
        handleChange({ target: { name: "bankName", value } });

        // If user is typing a custom bank name, allow manual IFSC entry
        setIsIfscManual(true);
        setIfscCode(""); // Reset IFSC since it's manual
    };

    return (
        <Autocomplete
            freeSolo
            fullWidth
            options={bankList}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
            loading={loading}
            onInputChange={handleInputChange}
            onChange={(event, newValue) => {
                if (newValue && typeof newValue === "object") {
                    setIfscCode(newValue.ifscGlobal);
                    handleChange({ target: { name: "bankName", value: newValue.name } });
                    setIsIfscManual(false); // Disable manual IFSC entry since it's selected
                } else {
                    setIfscCode("");
                    handleChange({ target: { name: "bankName", value: newValue || "" } });
                    setIsIfscManual(true); // Enable manual IFSC entry
                }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    label="Bank Name"
                    onBlur={handleBlur}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress color="inherit" size={20} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export default BankAutocomplete;
